"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import type { Address } from "viem";
import { useAccount } from "wagmi";
import { WalletAddress } from "@/components/core/WalletAddress/WalletAddress";
import { ResponsiveModal } from "@/components/features/staking/ResponsiveModal";
import { VoteDrawerForm } from "./VoteDrawerForm";
import { VoteDrawerSuccess } from "./VoteDrawerSuccess";

/**
 * Vote drawer orchestrator — Figma 5462:52047 / 52187 / 52328 (Phase 2).
 *
 * - Opens as bottom-sheet on mobile (≤767px) via ResponsiveModal.
 * - Shows UserIdentityStrip with wallet address + candidate nickname.
 * - Renders VoteDrawerForm; on success transitions to VoteDrawerSuccess.
 * - Reset to form state when drawer closes.
 *
 * AC-05 (responsive), AC-06 (identity strip), AC-15/AC-18 (success state).
 */

interface VoteDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: { address: Address; nickname: string };
}

export const VoteDrawer: React.FC<VoteDrawerProps> = ({
  open,
  onOpenChange,
  candidate,
}) => {
  const t = useTranslations("dao");
  const { address: userAddress } = useAccount();
  const [isSuccess, setIsSuccess] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Reset success state when drawer closes
      setIsSuccess(false);
    }
    onOpenChange(nextOpen);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={handleOpenChange}
      title={t("voting.drawer.title")}
      desktopMaxWidth="lg"
    >
      {/* UserIdentityStrip — Figma 5462:52052 */}
      <div className="flex h-10 items-center justify-between rounded-sm bg-bg-tertiary/60 px-2 py-1 gap-3 mb-4">
        {/* Right (start in RTL): candidate nickname */}
        <span className="text-xs font-medium text-text-primary">
          {candidate.nickname}
        </span>
        {/* Left (end in RTL): wallet address */}
        {userAddress && (
          <WalletAddress
            address={userAddress}
            canCopy={false}
            size={12}
            truncateSize="sm"
            addressClassName="text-xs mt-0 leading-[1.4]"
          />
        )}
      </div>

      {isSuccess ? (
        <VoteDrawerSuccess />
      ) : (
        <VoteDrawerForm
          candidate={candidate}
          onSuccess={() => setIsSuccess(true)}
        />
      )}
    </ResponsiveModal>
  );
};
